import { useEffect, useRef, useState } from "react";
import { Temporal } from "temporal-polyfill";

export function useNow(frequency?: number) {
  const [now, setNow] = useState(function () {
    return Temporal.Now.plainDateTimeISO("Asia/Tokyo");
  });

  useEffect(
    function () {
      if (frequency !== undefined) {
        const timer = setInterval(function () {
          setNow(function (previous) {
            const next = Temporal.Now.plainDateTimeISO("Asia/Tokyo");
            return Temporal.PlainDateTime.compare(previous, next) !== 0 ? next : previous;
          });
        }, frequency);

        return function () {
          clearInterval(timer);
        };
      }
    },
    [frequency],
  );

  return now;
}

function quantizeDateTime(dateTime: Temporal.PlainDateTime, type: "hour" | "minute" | "second") {
  switch (type) {
    case "hour":
      return dateTime.with({ minute: 0, second: 0, millisecond: 0 });
    case "minute":
      return dateTime.with({ second: 0, millisecond: 0 });
    case "second":
      return dateTime.with({ millisecond: 0 });
    default:
      return dateTime;
  }
}

export function useQuantizedDateTime(
  dateTime: Temporal.PlainDateTime,
  type: "hour" | "minute" | "second",
) {
  const [quantizedDateTime, setQuantizedDateTime] = useState(function () {
    return quantizeDateTime(dateTime, type);
  });

  useEffect(
    function () {
      setQuantizedDateTime(function (quantizedDateTime) {
        const newQuantizedDateTime = quantizeDateTime(dateTime, type);
        return Temporal.PlainDateTime.compare(quantizedDateTime, newQuantizedDateTime) !== 0
          ? newQuantizedDateTime
          : quantizedDateTime;
      });
    },
    [dateTime, type],
  );

  return quantizedDateTime;
}

export function useToday() {
  const timer = useRef<ReturnType<typeof setTimeout>>(null);
  const [today, setToday] = useState(function () {
    return Temporal.Now.plainDateISO("Asia/Tokyo");
  });

  useEffect(function () {
    timer.current = setTimeout(
      function () {
        setToday(Temporal.Now.plainDateISO("Asia/Tokyo"));
      },
      Temporal.Now.plainDateISO("Asia/Tokyo").add({ days: 1 }).toZonedDateTime("Asia/Tokyo")
        .epochMilliseconds - Date.now(),
    );
    return function () {
      if (timer.current !== null) {
        clearTimeout(timer.current);
      }
    };
  }, []);

  return today;
}
